import { fetchDispatch } from 'data/helpers'
import { showSnackbar, snackbarTypes } from 'src/redux/snackbar'

// Enums are serialized by name (JsonStringEnumConverter), so values are the C# enum member names.
// Must match server/Asclepius.Common/BackgroundJobs/BackgroundJobType.cs
const BACKGROUND_JOB_TYPE_NAMES = [
  'TowerHealthJobSync',
  'LegacyCsvImport',
  'UpdateEmbeddings',
  'UpdateJobEmbeddings',
  'UpdateSpecialtyEmbeddings',
  'UpdateSpecialtySynonymEmbeddings',
  'UpdateSubspecialtyEmbeddings',
  'UpdateSubspecialtySynonymEmbeddings',
  'UpdateEmployerEmbeddings',
  'UpdateCandidateEmbeddings',
  'UpdateGeocodingData',
  'UpdateJobCoordinates',
  'UpdateJobLocationCoordinates',
  'UserFilterAlerts',
]

export const BACKGROUND_JOB_TYPES = BACKGROUND_JOB_TYPE_NAMES.map(name => ({ value: name, label: name }))

export const getBackgroundJobTypeLabel = (value) => {
  const entry = BACKGROUND_JOB_TYPES.find(t => t.value === value)
  return entry ? entry.label : String(value)
}

// Must match server/Asclepius.Common/BackgroundJobs/Scheduled/ScheduledBackgroundJobKind.cs
export const SCHEDULED_BACKGROUND_JOB_KIND = {
  SYSTEM: 'System',
  ETL: 'Etl',
}

export const SCHEDULED_BACKGROUND_JOB_KINDS = [
  { value: SCHEDULED_BACKGROUND_JOB_KIND.SYSTEM, label: 'System' },
  { value: SCHEDULED_BACKGROUND_JOB_KIND.ETL, label: 'Etl' },
]

export const getScheduledBackgroundJobKindLabel = (value) => {
  const entry = SCHEDULED_BACKGROUND_JOB_KINDS.find(o => o.value === value)
  return entry ? entry.label : String(value)
}

// Action Types
const LIST_LOADING = 'scheduledBackgroundJobs/LIST_LOADING'
const LIST_SUCCESS = 'scheduledBackgroundJobs/LIST_SUCCESS'
const LIST_FAILURE = 'scheduledBackgroundJobs/LIST_FAILURE'

const SAVE_LOADING = 'scheduledBackgroundJobs/SAVE_LOADING'
const SAVE_SUCCESS = 'scheduledBackgroundJobs/SAVE_SUCCESS'
const SAVE_FAILURE = 'scheduledBackgroundJobs/SAVE_FAILURE'

// Thunks
export const fetchScheduledBackgroundJobs = () => {
  return async dispatch => {
    dispatch({ type: LIST_LOADING })
    try {
      const response = await fetchDispatch({
        path: '/api/scheduled-background-jobs',
        method: 'GET',
      })
      dispatch({ type: LIST_SUCCESS, payload: response.scheduledBackgroundJobs || [] })
      return response
    } catch (error) {
      dispatch({ type: LIST_FAILURE, payload: error.response || error.message })
      dispatch(showSnackbar('Failed to load scheduled background jobs', snackbarTypes.ERROR))
      throw error
    }
  }
}

export const saveScheduledBackgroundJob = (values) => {
  return async dispatch => {
    dispatch({ type: SAVE_LOADING })
    try {
      const response = await fetchDispatch({
        path: '/api/scheduled-background-jobs',
        method: 'POST',
        values,
      })
      dispatch({ type: SAVE_SUCCESS })
      dispatch(showSnackbar('Scheduled background job saved', snackbarTypes.SUCCESS))
      dispatch(fetchScheduledBackgroundJobs())
      return response
    } catch (error) {
      dispatch({ type: SAVE_FAILURE, payload: error.response || error.message })
      dispatch(showSnackbar('Failed to save scheduled background job', snackbarTypes.ERROR))
      throw error
    }
  }
}

export const deleteScheduledBackgroundJob = (kind, name) => {
  return async dispatch => {
    try {
      await fetchDispatch({
        path: '/api/scheduled-background-jobs/delete',
        method: 'POST',
        values: { kind, name },
      })
      dispatch(showSnackbar('Scheduled background job deleted', snackbarTypes.SUCCESS))
      dispatch(fetchScheduledBackgroundJobs())
    } catch (error) {
      dispatch(showSnackbar('Failed to delete scheduled background job', snackbarTypes.ERROR))
      throw error
    }
  }
}

export const runScheduledBackgroundJob = (kind, name) => {
  return async dispatch => {
    try {
      await fetchDispatch({
        path: '/api/scheduled-background-jobs/run',
        method: 'POST',
        values: { kind, name },
      })
      dispatch(showSnackbar('Scheduled background job enqueued', snackbarTypes.SUCCESS))
    } catch (error) {
      dispatch(showSnackbar('Failed to enqueue scheduled background job', snackbarTypes.ERROR))
      throw error
    }
  }
}

const initialState = {
  list: [],
  loading: false,
  saving: false,
  error: null,
}

export const scheduledBackgroundJobsReducer = (state = initialState, action) => {
  switch (action.type) {
    case LIST_LOADING:
      return { ...state, loading: true, error: null }
    case LIST_SUCCESS:
      return { ...state, loading: false, list: action.payload }
    case LIST_FAILURE:
      return { ...state, loading: false, error: action.payload }
    case SAVE_LOADING:
      return { ...state, saving: true, error: null }
    case SAVE_SUCCESS:
      return { ...state, saving: false }
    case SAVE_FAILURE:
      return { ...state, saving: false, error: action.payload }
    default:
      return state
  }
}
